import { Suspense, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Outlet, useLocation } from "react-router-dom";
import { SchemaOrg } from '@/components/seo/SchemaOrg';
import { generateOrganizationSchema, generateWebSiteSchema, generateProfessionalServiceSchema } from '@/utils/schemaGenerator';
import { ArrowUp } from 'lucide-react';

function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!visible) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Scroll to top"
      className="fixed top-1/2 -translate-y-1/2 right-4 z-50 w-11 h-11 rounded-full bg-primary text-white shadow-lg flex items-center justify-center hover:bg-primary/90 hover:scale-110 active:scale-95 transition-all duration-200"
    >
      <ArrowUp className="w-5 h-5" />
    </button>
  );
}
import { initThirdPartyScripts } from "@/lib/script-loader";
import { ExitIntentPopup } from '@/components/ui/ExitIntentPopup';
import { ThemeProvider } from '@/lib/theme';

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Let the new page render, then scroll to the anchored element
      const id = hash.replace('#', '');
      const attempt = (tries: number) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (tries > 0) {
          setTimeout(() => attempt(tries - 1), 150);
        }
      };
      setTimeout(() => attempt(5), 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    }
  }, [pathname, hash]);
  return null;
}

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (previously cacheTime)
    },
  },
});

const App = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Prevent browser from restoring scroll position on SPA navigation
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    initThirdPartyScripts();
  }, []);

  const organizationSchema = generateOrganizationSchema();
  const websiteSchema = generateWebSiteSchema();
  const professionalServiceSchema = generateProfessionalServiceSchema();
  // Home and About render their own Organization schema (with aggregateRating,
  // one of the few pages allowed to carry it) sharing the same #organization @id —
  // skip the site-wide copy there to avoid duplicate-@id GSC "invalid items" errors.
  const skipSitewideOrgSchema = pathname === '/' || pathname === '/about/';
  // The ProfessionalService block (12-service catalog, geo, hours) describes the
  // business itself, so it belongs on the business pages only. Elsewhere it added
  // 2-6KB of markup per page, and location pages already emit their own.
  const showProfessionalServiceSchema = ['/', '/about/', '/contact/', '/services/'].includes(pathname);

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        {!skipSitewideOrgSchema && <SchemaOrg schema={organizationSchema} />}
        <SchemaOrg schema={websiteSchema} />
        {showProfessionalServiceSchema && <SchemaOrg schema={professionalServiceSchema} />}
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <ScrollToTop />
          <ScrollToTopButton />
          <Suspense fallback={<PageLoader />}>
            <ExitIntentPopup />
            <Outlet />
          </Suspense>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default App;